import { useIsDesktop, useIsMobile } from 'hooks/use-size-class';
import styles from './style.module.scss';
import { useState, useEffect, useRef, RefObject } from 'react';
import Video from 'components/video';
import DownArrowIcon from 'components/icon/down-arrow-icon';

type CommercialHeroProps = {
  maxScale?: number;
  startingY?: number;
  onMinScale: () => void;
  onEnter: () => void;
  onLeave: () => void;
  videoSrc: string;
  scrollTargetRef?: RefObject<HTMLElement>;
};

export default function CommercialHero({
  maxScale = 80,
  startingY = 0,
  onMinScale,
  onEnter,
  onLeave,
  videoSrc,
  scrollTargetRef,
}: CommercialHeroProps) {
  const [scale, setScale] = useState(1);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const hasEntered = useRef(false);
  const heroRef = useRef<HTMLDivElement | null>(null);

  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();

  const scrollSensitivity = isDesktop ? 22 : isMobile ? 25 : 8 /* tablet */;
  const STICKY_WRAPPER_HEIGHT = 150; // in vh
  const MAX_SCROLL = STICKY_WRAPPER_HEIGHT * 200;

  const maskSize = 90 + (scale - 1) * 200;
  
  // Calculate arrow opacity: starts opaque, fades out linearly to 0 by 50% scroll
  const arrowOpacity = Math.max(0, 1.0 - (scrollProgress / 0.25));
  
  const arrowOnClick = () => {
    if (scrollTargetRef?.current) {
      scrollTargetRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (scale === 1) {
      hasEntered.current = false;
      onMinScale();
    } else if (!isVisible && hasEntered.current) {
      hasEntered.current = false;
      onLeave();
    } else if (isVisible && !hasEntered.current) {
      hasEntered.current = true;
      onEnter();
    }
  }, [scale, onMinScale, maxScale, isVisible, onEnter, onLeave]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = Math.max(window.scrollY - startingY, 0);
      const progress = Math.min((scrollY / MAX_SCROLL) * scrollSensitivity, 1);
      const newScale = 1 + (maxScale - 1) * progress;
      setScale(newScale);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollSensitivity, MAX_SCROLL, maxScale, startingY]);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    });

    observer.observe(el);
    return () => {
      observer.unobserve(el);
    };
  }, []);


  return (
    <div style={{ height: `${STICKY_WRAPPER_HEIGHT}vh` }}>
      <div className={styles.component} ref={heroRef}>
        <div className={styles.videoContainer}>
          <div 
            className={styles.downArrowWrapper} 
            style={{ opacity: arrowOpacity, pointerEvents: arrowOpacity > 0 ? 'auto' : 'none' }}
          >
            <DownArrowIcon 
              className={styles.downArrowIcon} 
              color="black" 
              onClick={arrowOnClick}
            />
          </div>
          <Video playsInline loop muted poster={'/next/assets/images/exp-commercial/commercial-hero-poster.webp'} src={videoSrc} type="video/webm" style={{ maskSize: `${maskSize}vw` }} />
        </div>
      </div>
    </div>
  );
}